<template>
  <b-card class="report-settings mb-3" no-body>
    <b-card-body class="py-3 px-4">
      <div class="report-settings__row">
        <div class="report-settings__field">
          <label class="report-settings__label" for="report-settings-auto-hide">
            Automatically hide reported content
          </label>
          <p class="report-settings__hint mb-0">
            When enabled, content is hidden as soon as it receives its first report.
          </p>
        </div>
        <b-form-checkbox
          id="report-settings-auto-hide"
          v-model="localAutoHide"
          switch
          class="report-settings__toggle"
        />
      </div>

      <hr class="my-3" />

      <div class="report-settings__row">
        <div class="report-settings__field">
          <label class="report-settings__label" for="report-settings-cap"> Daily report limit per person </label>
          <p class="report-settings__hint mb-0">
            Maximum number of reports a single employee can submit per day (0 = no limit). Used to prevent abuse and
            keep the moderation queue representative of genuine reports.
          </p>
        </div>
        <b-form-input
          id="report-settings-cap"
          v-model.number="localCap"
          type="number"
          min="0"
          step="1"
          class="report-settings__number-input"
        />
      </div>

      <div class="report-settings__footer mt-3">
        <b-alert :show="!!saveError" variant="danger" class="mb-2 py-2">{{ saveError }}</b-alert>
        <b-button variant="primary" size="sm" :disabled="isSaving || !isDirty || !capIsValid" @click="onSave">
          <b-spinner v-if="isSaving" small class="mr-1" />
          Save
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import State from '@/state/State';
import { updateRecognitionReportSettings } from '@/queries/recognition';
import { RECOGNITION_REPORT_SETTINGS_DEFAULTS } from '@/pages/recognition/constants';

const contentModeration = computed(() => State.state.company?.properties?.content_moderation || {});

const currentAutoHide = computed(() => {
  const val = contentModeration.value.hide_reported_by_default;
  return val === undefined || val === null ? RECOGNITION_REPORT_SETTINGS_DEFAULTS.autoHide : Boolean(val);
});

const currentCap = computed(() => {
  const val = contentModeration.value.max_reports_per_day;
  return val === undefined || val === null ? RECOGNITION_REPORT_SETTINGS_DEFAULTS.maxReportsPerDay : Number(val);
});

// Seed the form from the current server values at mount. The parent modal
// renders this component with `lazy`, so it remounts on every open and these
// refs always reflect the latest persisted settings — no server-push watcher
// is needed (and a watcher wouldn't fire reliably anyway, since State.state is
// reassigned wholesale by setMe rather than mutated in place).
const localAutoHide = ref(currentAutoHide.value);
const localCap = ref(currentCap.value);

// Empty / non-integer / negative cap is invalid. Without this guard, clearing
// the number field yields '' and would silently save 0 (= "no limit").
const capIsValid = computed(() => Number.isInteger(localCap.value) && localCap.value >= 0);

const isDirty = computed(() => localAutoHide.value !== currentAutoHide.value || localCap.value !== currentCap.value);

const emit = defineEmits(['saved']);

const saveError = ref('');

const saveMutation = useMutation(updateRecognitionReportSettings());
const isSaving = computed(() => saveMutation.isPending.value);

const onSave = () => {
  saveError.value = '';
  saveMutation.mutate(
    { autoHide: localAutoHide.value, maxReportsPerDay: Number(localCap.value) },
    {
      onSuccess: () => {
        // Close the modal directly on success (no inline confirmation).
        emit('saved');
      },
      onError: () => {
        saveError.value = 'Failed to save settings. Please try again.';
      },
    },
  );
};
</script>

<style lang="scss" scoped>
.report-settings {
  border: 1px solid var(--palette-color-default-lighten-90);

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
  }

  &__field {
    flex: 1;
  }

  &__label {
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
    color: var(--palette-color-default);
  }

  &__hint {
    font-size: 0.8125rem;
    color: var(--palette-color-default-lighten-40);
  }

  &__toggle {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  &__number-input {
    width: 3.25rem;
    flex-shrink: 0;
    text-align: center;
    padding: 0.25rem 0.375rem;

    // Remove the native number-input spin buttons (up/down arrows).
    appearance: textfield;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
  }
}
</style>
